import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import type {LocalDate} from '../../domain/local-date.value-object';
import {isLocalDate} from '../../lib/local-date';

export interface DashboardQuery {
  readonly date: LocalDate | null;
}

/**
 * Validates the optional `?date=YYYY-MM-DD` search parameter that drives
 * the dashboard route. Empty/missing input is normalised to `null` so the
 * server may resolve "today" from the user's timezone.
 */
export class DashboardQueryValidator implements Validator<DashboardQuery> {
  readonly standardSchema = z
    .strictObject({
      date: z
        .string()
        .refine(isLocalDate, {message: 'Date must be formatted as YYYY-MM-DD.'})
        .optional(),
    })
    .transform(input => ({date: input.date ?? null}));

  validate(value: unknown): Result<DashboardQuery, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);

    if (!parsed.success) {
      return err(
        new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid dashboard query.'),
      );
    }

    return ok(parsed.data);
  }
}
